import React, { useEffect, useMemo, useState, useRef } from "react";
import Particles, { initParticlesEngine } from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim";
import {
  Container, Typography, useMediaQuery, useTheme, Grid,
  Box, Link, Accordion, AccordionSummary, AccordionDetails, Button
} from '@mui/material';
import { useSpring, animated } from '@react-spring/web';
import WebIcon from '@mui/icons-material/Web';
import SeoIcon from '@mui/icons-material/Search';
import UxUiIcon from '@mui/icons-material/DesignServices';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { motion, useInView, animate } from 'framer-motion';
import Gallery3D from './Gallery3D';

// ─── COUNTER ────────────────────────────────────────────────────
const Counter = ({ target, suffix = '' }) => {
  const ref = useRef(null);
  const iv = useInView(ref, { once: true, margin: '-40px' });
  const [v, setV] = useState(0);
  useEffect(() => { if (iv) { const c = animate(0, target, { duration: 2.2, ease: 'easeOut', onUpdate: x => setV(Math.round(x)) }); return () => c.stop(); } }, [iv, target]);
  return <Typography ref={ref} sx={{ fontSize: { xs: '4rem', md: '7rem' }, fontWeight: 900, color: 'white', lineHeight: 1, fontFamily: 'Montserrat, sans-serif', letterSpacing: '-0.03em' }}>{v}{suffix}</Typography>;
};

// ════════════════════════════════════════════════════════════════
const Home = () => {
  const [init, setInit] = useState(false);
  const t = useTheme();
  const sm = useMediaQuery(t.breakpoints.down('sm'));
  const noS = { userSelect: 'none', WebkitUserSelect: 'none' };

  useEffect(() => { initParticlesEngine(async e => { await loadSlim(e); }).then(() => setInit(true)); }, []);

  const opts = useMemo(() => ({
    background: { color: { value: '#000000' } },
    fpsLimit: 30,
    interactivity: { events: { onHover: { enable: true, mode: 'repulse' } }, modes: { repulse: { distance: 100, duration: 0.4 } } },
    particles: {
      color: { value: '#ffffff' },
      links: { color: '#ffffff', distance: 150, enable: true, opacity: 0.06, width: 0.5 },
      move: { enable: true, outModes: { default: 'bounce' }, random: true, speed: 0.25, straight: false },
      number: { value: sm ? 25 : 60, density: { enable: true, area: 1000 } },
      opacity: { value: { min: 0.04, max: 0.15 } },
      shape: { type: 'circle' },
      size: { value: { min: 0.3, max: 1.5 } },
    },
    detectRetina: false,
  }), [sm]);

  const logoS = useSpring({ opacity: init ? 1 : 0, y: init ? 0 : 20, delay: 100, config: { duration: 900 } });
  const t1 = useSpring({ opacity: init ? 1 : 0, y: init ? 0 : 20, delay: 300, config: { duration: 900 } });
  const t2 = useSpring({ opacity: init ? 1 : 0, y: init ? 0 : 20, delay: 500, config: { duration: 900 } });
  const t3 = useSpring({ opacity: init ? 1 : 0, delay: 800, config: { duration: 1000 } });

  const services = [
    { n: '01', t: 'Web Design', d: 'Maßgeschneiderte Websites — vom Unternehmensauftritt bis E-Commerce. Performance, Responsivität, Conversion.', i: <WebIcon sx={{ fontSize: 28 }} /> },
    { n: '02', t: 'SEO', d: 'Datengetriebene Optimierung für bessere Rankings. Organischer Traffic, qualifizierte Leads, messbare Resultate.', i: <SeoIcon sx={{ fontSize: 28 }} /> },
    { n: '03', t: 'UX/UI Design', d: 'User-zentrierte Interfaces — Wireframe bis Prototyp. Intuitiv, reduziert, wirkungsvoll.', i: <UxUiIcon sx={{ fontSize: 28 }} /> },
  ];

  const faqs = [
    { q: 'Wie lange dauert eine Website?', a: 'Je nach Umfang 4–12 Wochen. Wir kommunizieren transparent und halten Deadlines.' },
    { q: 'Welche Marketing-Strategien bieten Sie?', a: 'SEO, SEA, Social Media, Content & E-Mail-Marketing — abgestimmt auf Ihre Ziele.' },
    { q: 'Kann ich Referenzen einsehen?', a: 'Ja — unser Portfolio zeigt reale Projekte. Für Case Studies sprechen Sie uns an.' },
    { q: 'Wie sichern Sie meine Website?', a: 'SSL, Updates, Angriffshärtung und DSGVO-konforme Dokumente sind Standard.' },
  ];

  return (
    <Box sx={{ bgcolor: 'black', position: 'relative' }}>
      {/* PARTICLES */}
      <Box sx={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }}>{init && <Particles id="tp" options={opts} style={{ position: 'absolute', inset: 0 }} />}</Box>

      {/* ═══════════════ HERO ═══════════════ */}
      <Box sx={{ position: 'relative', zIndex: 2, minHeight: '100vh', display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, pt: { xs: 14, md: 0 }, px: { xs: 3, md: 8 } }}>
        <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto' }}>
          <animated.div style={{ opacity: logoS.opacity, transform: logoS.y.to(y => `translateY(${y}px)`) }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: { xs: 4, md: 6 } }}>
              <Box sx={{ width: 40, height: '1px', bgcolor: 'rgba(255,255,255,0.4)' }} />
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.4em', textTransform: 'uppercase', fontFamily: 'monospace' }}>
                MJ — Webagentur · est. 2023
              </Typography>
            </Box>
          </animated.div>

          <animated.div style={{ opacity: t1.opacity, transform: t1.y.to(y => `translateY(${y}px)`) }}>
            <Typography sx={{ color: 'white', fontWeight: 900, fontSize: { xs: '2.4rem', sm: '5rem', md: '8rem' }, lineHeight: 0.9, letterSpacing: '-0.04em', fontFamily: 'Montserrat, sans-serif', overflowWrap: 'anywhere', ...noS }}>
              MJ<br />
              <Box
                component="span"
                sx={{
                  color: 'transparent',
                  WebkitTextStroke: { xs: '1px rgba(255,255,255,0.55)', md: '1.5px rgba(255,255,255,0.55)' },
                  WebkitTextFillColor: 'transparent',
                }}
              >
                WEBAGENTUR
              </Box>
            </Typography>
          </animated.div>

          <animated.div style={{ opacity: t2.opacity, transform: t2.y.to(y => `translateY(${y}px)`) }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mt: { xs: 4, md: 6 }, flexWrap: 'wrap', gap: 2 }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: { xs: '0.9rem', md: '1.1rem' }, fontWeight: 300, maxWidth: 500, letterSpacing: '0.02em' }}>
                Wir entwickeln digitale Produkte, die Marken definieren — durch Design, Technologie und Strategie.
              </Typography>
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 0.5 }}>
                {['D','E','S','I','G','N','•','C','O','D','E','•','S','E','O'].map((l, i) => (
                  <Typography key={i} sx={{ color: l === '•' ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.5)', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.15em', fontFamily: 'monospace' }}>{l}</Typography>
                ))}
              </Box>
            </Box>
          </animated.div>

          <animated.div style={{ opacity: t3.opacity }}>
            <Button component={Link} href="mailto:info@mj-webagentur.de" sx={{ mt: { xs: 4, md: 5 }, color: 'white', border: '1px solid rgba(255,255,255,0.2)', px: 4, py: 1, borderRadius: 0, fontSize: '0.8rem', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', '&:hover': { bgcolor: 'white', color: 'black', borderColor: 'white' }, transition: 'all 0.4s' }}>Projekt starten →</Button>
          </animated.div>
        </Box>
      </Box>

      {/* ═══════════════ PROJECTS — 3D gallery flythrough ═══════════════ */}
      <Gallery3D />

      {/* ═══════════════ SERVICES ═══════════════ */}
      <Box sx={{ position: 'relative', zIndex: 2, py: { xs: 10, md: 16 }, px: { xs: 2, md: 8 } }}>
        <Container maxWidth="lg">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.3em', mb: 6, textTransform: 'uppercase' }}>Leistungen</Typography>
          </motion.div>
          <Grid container spacing={4}>
            {services.map((s, i) => (
              <Grid item xs={12} md={4} key={i}>
                <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}>
                  <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', pt: 3 }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.2em', mb: 2 }}>{s.n}</Typography>
                    <Box sx={{ color: 'white', mb: 2, opacity: 0.6 }}>{s.i}</Box>
                    <Typography variant="h5" sx={{ color: 'white', fontWeight: 800, mb: 1.5, fontFamily: 'Montserrat, sans-serif', fontSize: '1.3rem' }}>{s.t}</Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.85rem', lineHeight: 1.8, maxWidth: 300 }}>{s.d}</Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════ STATS ═══════════════ */}
      <Box sx={{ position: 'relative', zIndex: 2, py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="baseline">
            {[
              { v: 50, s: '+', l: 'Abgeschlossene Projekte' },
              { v: 3, s: '', l: 'Jahre am Markt' },
              { v: 100, s: '%', l: 'Zufriedene Kunden' },
            ].map((s, i) => (
              <Grid item xs={12} md={4} key={i}>
                <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.12 }}>
                  <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.08)', pt: 3 }}>
                    <Counter target={s.v} suffix={s.s} />
                    <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.15em', mt: 1, textTransform: 'uppercase' }}>{s.l}</Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════ MARQUEE ═══════════════ */}
      <Box sx={{ position: 'relative', zIndex: 2, borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: 3, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', animation: 'scroll 30s linear infinite', width: 'max-content', gap: 6 }}>
          {[...Array(2)].map((_, j) => ['Web Design', 'SEO', 'UX/UI', 'Entwicklung', 'Branding', 'E-Commerce', 'Strategie', 'Performance'].map((w, i) => (
            <Typography key={`${j}-${i}`} sx={{ color: i % 3 === 0 ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.15)', fontSize: { xs: '1.5rem', md: '2.5rem' }, fontWeight: 900, whiteSpace: 'nowrap', letterSpacing: '-0.02em', fontFamily: 'Montserrat, sans-serif', transition: 'color 0.3s', '&:hover': { color: 'white' } }}>{w}</Typography>
          )))}
        </Box>
      </Box>

      {/* ═══════════════ FAQ ═══════════════ */}
      <Box sx={{ position: 'relative', zIndex: 2, py: { xs: 10, md: 16 }, px: { xs: 2, md: 8 } }}>
        <Container maxWidth="md">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.3em', mb: 6, textTransform: 'uppercase' }}>Fragen</Typography>
          </motion.div>
          {faqs.map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.35, delay: i * 0.06 }}>
              <Accordion sx={{ bgcolor: 'transparent', color: 'white', borderBottom: '1px solid rgba(255,255,255,0.06)', boxShadow: 'none', '&:before': { display: 'none' }, '&.Mui-expanded': { margin: 0 } }} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.3)' }} />} sx={{ px: 0, '& .MuiAccordionSummary-content': { my: 2 } }}>
                  <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.95rem', md: '1.1rem' } }}>{f.q}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, pb: 3 }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.9rem', lineHeight: 1.8, maxWidth: 600 }}>{f.a}</Typography>
                </AccordionDetails>
              </Accordion>
            </motion.div>
          ))}
        </Container>
      </Box>

      {/* ═══════════════ CTA — direct contact ═══════════════ */}
      <Box sx={{ position: 'relative', zIndex: 2, py: { xs: 12, md: 20 }, px: { xs: 2, md: 8 }, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <Container maxWidth="lg">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <Grid container spacing={4} alignItems="flex-end">
              <Grid item xs={12} md={8}>
                <Typography sx={{ color: 'white', fontWeight: 900, fontSize: { xs: '2rem', sm: '2.8rem', md: '5rem' }, lineHeight: 0.95, letterSpacing: '-0.03em', fontFamily: 'Montserrat, sans-serif' }}>
                  Bereit für<br />Ihr nächstes<br />Projekt?
                </Typography>
                <Link
                  href="mailto:info@mj-webagentur.de"
                  underline="none"
                  sx={{
                    display: 'inline-block',
                    mt: { xs: 3, md: 5 },
                    color: 'white',
                    fontFamily: 'Montserrat, sans-serif',
                    fontWeight: 800,
                    fontSize: { xs: '0.95rem', sm: '1.2rem', md: '1.8rem' },
                    letterSpacing: '-0.02em',
                    borderBottom: '1px solid rgba(255,255,255,0.3)',
                    pb: 0.5,
                    wordBreak: 'break-word',
                    transition: 'border-color 0.3s, color 0.3s',
                    '&:hover': { borderColor: 'white', color: 'white' },
                  }}
                >
                  info@mj-webagentur.de →
                </Link>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.9rem', lineHeight: 1.8 }}>
                  Kein Briefing zu klein, keine Vision zu groß. Schreiben Sie uns — wir antworten in der Regel innerhalb von 24 Stunden.
                </Typography>
              </Grid>
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* ═══════════════ FOOTER ═══════════════ */}
      <Box component="footer" sx={{ position: 'relative', zIndex: 2, borderTop: '1px solid rgba(255,255,255,0.08)', py: { xs: 6, md: 8 }, px: { xs: 3, md: 8 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="flex-start">
            {/* Brand */}
            <Grid item xs={12} md={5}>
              <Typography sx={{ color: 'white', fontFamily: 'Montserrat, sans-serif', fontWeight: 900, fontSize: '1.4rem', letterSpacing: '-0.02em' }}>
                MJ Webagentur
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', mt: 1, lineHeight: 1.7, maxWidth: 360 }}>
                Digitale Produkte mit Substanz — Design, Code, SEO.
              </Typography>
            </Grid>

            {/* Contact */}
            <Grid item xs={12} sm={6} md={3}>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem', fontWeight: 700, letterSpacing: { xs: '0.2em', md: '0.3em' }, textTransform: 'uppercase', mb: 2 }}>
                Kontakt
              </Typography>
              <Link href="mailto:info@mj-webagentur.de" underline="none" sx={{ display: 'inline-block', color: 'white', fontSize: '0.9rem', mb: 0.8, wordBreak: 'break-word', '&:hover': { color: 'rgba(255,255,255,0.7)' } }}>
                info@mj-webagentur.de
              </Link>
            </Grid>

            {/* Legal */}
            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem', fontWeight: 700, letterSpacing: { xs: '0.2em', md: '0.3em' }, textTransform: 'uppercase', mb: 2 }}>
                Rechtliches
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                <Link href="/impressum" underline="none" sx={{ color: 'white', fontSize: '0.9rem', '&:hover': { color: 'rgba(255,255,255,0.7)' } }}>Impressum</Link>
                <Link href="/dsgvo" underline="none" sx={{ color: 'white', fontSize: '0.9rem', '&:hover': { color: 'rgba(255,255,255,0.7)' } }}>Datenschutz</Link>
              </Box>
            </Grid>
          </Grid>

          {/* Bottom bar */}
          <Box sx={{ mt: { xs: 5, md: 8 }, pt: 3, borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.75rem', fontFamily: 'monospace' }}>
              © {new Date().getFullYear()} MJ Webagentur · Alle Rechte vorbehalten
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.65rem', letterSpacing: '0.3em', textTransform: 'uppercase', fontFamily: 'monospace' }}>
              Made in Germany
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
